import { useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { countActiveFilters } from '@/utils/filters';
import type { LeaveApplicationFilters } from '../types/filters.types';
import {
  clearDependentFilters,
  resolveDateRange,
  toLeaveApplicationFilters,
  toLeaveApplicationSearch,
  toSummaryParams,
} from '../utils/leaveApplicationFilters';

const route = getRouteApi('/_auth/hr-form/leave-applications/');

export function useLeaveApplicationFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);

  const appliedFilters = useMemo(() => toLeaveApplicationFilters(search), [search]);

  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues: appliedFilters,
    onApply: (values) => {
      void navigate({
        search: (previous) => toLeaveApplicationSearch(values, previous.search ?? ''),
        replace: true,
      });
    },
    shouldWaitForApply: true,
  });

  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({ search: (previous) => ({ ...previous, search: text }), replace: true });
    },
  });

  const filterValues = useMemo(
    () => ({ ...draftValues, ...resolveDateRange(draftValues) }),
    [draftValues],
  );

  const listParams = useMemo(
    () => toSummaryParams(appliedFilters, search.search ?? ''),
    [appliedFilters, search.search],
  );

  function handleFilterChange(next: LeaveApplicationFilters) {
    setDraftValues((previous) => clearDependentFilters(previous, next));
  }

  function applyFilterValues(next: LeaveApplicationFilters) {
    applyValues(clearDependentFilters(draftValues, next));
  }

  const activeFilterCount = countActiveFilters(appliedFilters);

  return {
    searchText,
    filterValues,
    filterOpen,
    setFilterOpen,
    activeFilterCount,
    hasPendingChanges,
    listParams,
    handleSearchChange: setSearchText,
    handleFilterChange,
    applyFilterValues,
  };
}
